import { getActiveCarts } from "@/redux/reducers/checkout/thunk";
import { getAllCarts } from "@/redux/selectors";
import { useSignedIn } from "@/utils/hooks/useSignedIn";
import { useTrackActions, useTrackViewEvent } from "@/utils/hooks/useTracking";
import {
  AnalyticsTrackingEvent,
  Button,
  getEntityImage,
  MOBILE_SCREEN_SIZE,
  TABLET_SCREEN_SIZE,
  UserRetailerShoppingCart,
} from "@brandclub/common-ui";
import { Box, styled, Typography, useTheme } from "@mui/material";
import _isEqual from "lodash/isEqual";
import pluralize from "pluralize";
import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { sumItemQuantities } from "../../utils/misc";
import Loading from "../Loading";
import LandingPageWrapper from "../pages/AppPages/Pages/components/LandingPageWrapper";
import { UserLoginContext } from "../pages/Auth/UserLoginProvider";
import { CartProduct } from "./components/CartProduct";
import { EmptyBagMessageCard } from "./EmptyBagMessageCard";

import { useRetailerName } from "@/utils/hooks/useRetailerName";
import { calculateTotal } from "./utils";
import { useSplitTreatments } from "@splitsoftware/splitio-react";

const StyledLandingPageWrapper = styled(LandingPageWrapper)({
  flex: 1,
  height: "100%",
  ".my-bag__wrapper": {
    flex: 1,
    paddingBottom: 105,
  },
});

const MyBagContainer = styled("div")({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  maxWidth: 735,
  padding: "0",
  alignItems: "center",
  justifyContent: "center",
  gap: 40,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    gap: 30,
  },
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    gap: 30,
  },
  ".head": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 15,
    maxWidth: 552,
    marginTop: 95,
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      alignSelf: "flex-start",
      marginTop: 48,
    },

    ".title": {
      fontSize: 24,
      fontWeight: 700,
      textAlign: "center",
      [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        fontSize: 22,
        textAlign: "left",
      },
    },
  },
  ".product_section": {
    width: "100%",
    display: "flex",

    flexDirection: "column",
    gap: 31,
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      gap: 31,
    },
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      gap: 20,
    },
    ".retailer_cart + .retailer_cart": {
      borderTop: "1.2px solid #dcdddc",
      paddingTop: 31,
      [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        paddingTop: 20,
      },
    },
    ".retailer": {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: 18,

      ".retailer_logo": {
        width: 30,
        height: 30,
        objectFit: "contain",
        [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
          width: 25,
          height: 25,
        },
      },
      ".retailer_name": {
        flex: 1,
        fontSize: 18,
        fontWeight: 600,
        [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
          fontSize: 18,
          fontWeight: 600,
        },
      },
      ".actions": {},
    },
    ".group_info": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: 20,
      marginBottom: 30,
      [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        marginTop: 10,
        marginBottom: 24,
      },
      ".group_count": {
        fontSize: 16,
        fontWeight: 500,
        [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
          fontSize: 14,
        },
      },
      ".group_subtotal": {
        fontSize: 16,
        fontWeight: 500,
        [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
          fontSize: 14,
        },
        ".price": {
          fontWeight: 700,
        },
      },
    },

    ".product": {
      width: "100%",
    },
  },
});

const StyledEmptyBagMessageCard = styled(EmptyBagMessageCard)({
  margin: "auto",
  marginTop: 200,
  marginBottom: 200,
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    margin: "32px auto 0 auto",
  },
});

const OverAllCheckoutButton = styled(Button)({
  fontSize: 16,
  height: 50,

  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    fontSize: 14,
    height: 44,
    marginTop: 23,
  },
});

interface ProductSectionProps {
  items: UserRetailerShoppingCart[];
  onRetailerCheckout: (item: UserRetailerShoppingCart) => Promise<void>;
}

const SingleRetailerSection = ({
  item,
  onRetailerCheckout,
}: {
  item: UserRetailerShoppingCart;
  onRetailerCheckout: (item: UserRetailerShoppingCart) => Promise<void>;
}) => {
  const retailerName = useRetailerName(item.retailerId);
  return (
    <Box
      key={`cartGroup:${item.shoppingCartId}`}
      display="flex"
      flexDirection="column"
      className="retailer_cart"
      gap={0}
    >
      <Box className="retailer">
        <img
          src={getEntityImage(item.retailerId, "retailer")}
          alt={`Logo for retailer: ${item.retailerId}`}
          className="retailer_logo"
        />
        <Typography className="retailer_name">{retailerName}</Typography>
      </Box>
      <Box className="group_info">
        <Box className="group_count">
          {pluralize("item", sumItemQuantities(item?.items), true)}
        </Box>
        <Box className="group_subtotal">
          Subtotal:{" "}
          <span className="price">{calculateTotal(item?.items ?? [])}</span>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "40px",
          marginBottom: "30px",
          [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
            gap: "30px",
            marginBottom: "20px",
          },
        }}
      >
        {(item?.items ?? []).map((product) => (
          <CartProduct
            large
            product={product}
            key={`cart:${item?.shoppingCartId}:item:${product.stacklineSku}`}
          />
        ))}
      </Box>
      <OverAllCheckoutButton
        disableMaxWidth
        onClick={async () => {
          await onRetailerCheckout(item);
        }}
      >
        Checkout {retailerName}
      </OverAllCheckoutButton>
    </Box>
  );
};

const ProductSection = (props: ProductSectionProps) => {
  return (
    <Box className="product_section">
      {/* Render all carts (those with > 0 items - filtered by redux already) */}
      {props.items.map((item, _index, allCarts) => (
        <SingleRetailerSection
          key={`cartGroup:${item.shoppingCartId}`}
          item={item}
          onRetailerCheckout={props.onRetailerCheckout}
        />
      ))}
    </Box>
  );
};

const MyBag = () => {
  const hasOptimisticItems = useAppSelector(
    (state) => !!state.checkout.optimisticAllCarts
  );
  const loadingCarts = useAppSelector((state) => state.checkout.loadingCarts);
  const storeDomainBrandingType = useAppSelector(
    (state) => state.appConfig?.domainConfig.storeBrandingType
  );
  const domainName = useAppSelector(
    (state) => state.appConfig?.domainConfig.domainName
  );
  const { send: userLoginSend } = useContext(UserLoginContext);
  const { signedIn } = useSignedIn();
  const allCarts = useAppSelector(getAllCarts, _isEqual);
  const cartProducts = useAppSelector((state) => state.checkout.products);
  const userProfile = useAppSelector(({ userProfile }) => userProfile);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [trackAction] = useTrackActions();
  const trackViewBagEvent = useTrackViewEvent(AnalyticsTrackingEvent.VIEW_BAG);

  const { treatments } = useSplitTreatments({
    names: ["brandclub_dtc_checkout_simplified"],
    attributes: {
      email: userProfile?.email ?? "",
      appId: domainName ?? "",
    },
  });

  const useSimplifiedCheckout =
    treatments?.brandclub_dtc_checkout_simplified?.treatment === "on";

  const theme = useTheme();

  useEffect(() => {
    !hasOptimisticItems && dispatch(getActiveCarts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const cartItems = allCarts.flatMap((cart) =>
      cart.items?.map((item) => {
        const product = cartProducts?.[item.stacklineSku];
        return {
          retailerId: item.retailerId,
          retailerSku: item.retailerSku,
          stacklineSku: item.stacklineSku,
          quantity: item.quantity,
          retailPrice: item.retailPrice,
          brandId: item.brandId,
          productName: product?.title,
          // brandName: product?.brandName,
          // categoryId: product?.categoryId,
          // categoryName: product?.categoryName,
          // subCategoryId: product?.subCategoryId,
          // subCategoryName: product?.subCategoryName,
        };
      })
    );

    trackViewBagEvent({
      cartItems,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckout = (retailerId: number) => {
    const retailerIds = allCarts.map((cart) => cart.retailerId);
    trackAction(
      AnalyticsTrackingEvent.BC_USER_REQUESTED_CHECKOUT,
      {
        cartItems: allCarts.map((cart) => ({
          items: cart.items,
          retailerId: cart.retailerId,
        })),
        retailerIds,
        retailerId: retailerIds[0],
        source: "mybag",
      },
      {
        sendImmediately: true,
      }
    );
    const checkoutPath = useSimplifiedCheckout
      ? "/checkout_simplified"
      : "/checkout";
    navigate(`${checkoutPath}?retailerId=${retailerId}`);
  };

  return (
    <StyledLandingPageWrapper
      wrapperClassName="my-bag__wrapper"
      className="my-bag__container"
    >
      {loadingCarts ? (
        <Loading dot></Loading>
      ) : (
        <MyBagContainer
          style={{ maxWidth: allCarts.length > 0 ? undefined : "100%" }}
        >
          {allCarts.length > 0 ? (
            <>
              <div className="head">
                <div className="title">
                  {theme.globalComponentPresets?.checkoutDrawer
                    ?.customHeaderName || "Your Bag"}
                </div>
              </div>

              <ProductSection
                items={allCarts as any}
                onRetailerCheckout={async (selectedCart) => {
                  handleCheckout(selectedCart.retailerId);
                }}
              />
            </>
          ) : (
            <StyledEmptyBagMessageCard />
          )}
        </MyBagContainer>
      )}
    </StyledLandingPageWrapper>
  );
};

export default MyBag;
